import { createRef, useEffect, useRef } from 'react'
import { MdClose, MdOpenInNew, MdPrint } from 'react-icons/md'

interface ReceiptPreviewModalProps {
    isOpen: boolean
    onClose: () => void
    url: string
    name?: string
    /**
     * Força o modo de exibição quando a URL não tem extensão — é o caso do recibo
     * gerado na hora, que chega como `blob:` (a rota exige token, então não dá para
     * apontar o iframe direto para a API).
     */
    forceType?: 'pdf' | 'image'
}

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp']

/**
 * Visualizador do comprovante. Imagem renderiza inline; PDF vai num `<iframe>`
 * (o viewer nativo do browser). Qualquer outro formato (DOC, XLS) não tem
 * preview — sobra o link para abrir em outra aba.
 */
export default function ReceiptPreviewModal({
    isOpen,
    onClose,
    url,
    name = 'Comprovante',
    forceType,
}: ReceiptPreviewModalProps) {
    const modalRef = createRef<HTMLDialogElement>()
    const frameRef = useRef<HTMLIFrameElement | null>(null)

    useEffect(() => {
        if (isOpen) {
            modalRef.current?.showModal()
        } else {
            modalRef.current?.close()
        }
    }, [isOpen])

    const path = url.split('?')[0].toLowerCase()
    const isImage =
        forceType === 'image' ||
        (!forceType && IMAGE_EXTENSIONS.some(ext => path.endsWith(ext)))
    const isPdf = forceType === 'pdf' || (!forceType && path.endsWith('.pdf'))

    // O blob é same-origin, então o `print()` do iframe manda direto para a
    // impressora sem passar pela barra do viewer.
    const print = () => {
        const frame = frameRef.current
        if (!frame?.contentWindow) return
        frame.contentWindow.focus()
        frame.contentWindow.print()
    }

    return (
        <dialog className="modal z-50" ref={modalRef} onClose={onClose}>
            <div className="modal-box !max-w-4xl !mx-4 !p-0">
                <div className="flex items-center justify-between p-4 border-b border-base-300">
                    <h2 className="text-lg font-bold truncate pr-4">{name}</h2>
                    <div className="flex items-center gap-1 shrink-0">
                        {isPdf && (
                            <button
                                type="button"
                                onClick={print}
                                className="btn btn-sm btn-ghost gap-1"
                                title="Imprimir"
                            >
                                <MdPrint className="text-base" />
                                <span className="hidden sm:inline">Imprimir</span>
                            </button>
                        )}
                        <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-ghost gap-1"
                            title="Abrir em nova aba"
                        >
                            <MdOpenInNew className="text-base" />
                            <span className="hidden sm:inline">Abrir</span>
                        </a>
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-sm btn-ghost btn-square"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    </div>
                </div>

                <div className="bg-base-200/40 p-4">
                    {isImage && (
                        <img
                            src={url}
                            alt={name}
                            className="max-w-full max-h-[70vh] mx-auto rounded-lg"
                        />
                    )}

                    {isPdf && (
                        <iframe
                            ref={frameRef}
                            src={url}
                            title={name}
                            className="w-full h-[70vh] rounded-lg bg-base-100"
                        />
                    )}

                    {!isImage && !isPdf && (
                        <div className="text-center py-12">
                            <p className="text-base-content/70">
                                Este formato não tem visualização no navegador.
                            </p>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-secondary btn-sm gap-1 mt-4"
                            >
                                <MdOpenInNew className="text-base" />
                                Baixar comprovante
                            </a>
                        </div>
                    )}
                </div>
            </div>

            <form method="dialog" className="modal-backdrop">
                <button onClick={onClose}>close</button>
            </form>
        </dialog>
    )
}
